import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import AnchorMenu from '@/components/organisms/anchor-menu/anchor-menu';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useResizeObserver from '@/libraries/hooks/use-resize-observer';
import styles from './in-page-navigation-bar.module.scss';

interface InPageNavigationBarProps extends ModuleWrapperProps {
  /** The links of the in-page navigation */
  links: Content.Properties.Link[];
}

const InPageNavigationBar = ({ links, ...rest }: InPageNavigationBarProps) => {
  const {
    hotelDetails,
    isScrolled,
    setHasInPageNavigation,
    setStickyTopContentHeight,
  } = usePageDetailsContext();
  const [isAtTop, setIsAtTop] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const componentRef = useRef<HTMLDivElement>(null);
  const inPageNavigationHeight = useResizeObserver(componentRef);
  const lastScrollY = useRef(0);

  useEffect(() => {
    setHasInPageNavigation(true);

    return () => {
      setHasInPageNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isScrolled) return;

    setStickyTopContentHeight(isAtTop ? inPageNavigationHeight : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAtTop, isScrolled, inPageNavigationHeight]);

  const handleScroll = () => {
    if (!componentRef?.current) return;

    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > lastScrollY.current;
    const elementTop = componentRef.current.getBoundingClientRect().top;
    const isSticky = elementTop <= 0;

    setIsAtTop(isSticky);

    // Show if: not sticky OR sticky and scrolling down OR at initial position (scrollY === 0)
    setIsVisible(!isSticky || isScrollingDown || currentScrollY === 0);

    lastScrollY.current = currentScrollY;
  };

  useEffect(() => {
    if (!window) return;

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModuleWrapper
      {...rest}
      allowOverflow
      className={classNames(styles.component, {
        [styles.hasAnchorMenu]: links.length > 0,
        [styles.isVisible]: isVisible,
        [styles.isHotelPage]: hotelDetails,
      })}
      hasInPageNavigation={!hotelDetails}
      id="anchorMenu"
      padding="none"
    >
      <div ref={componentRef} className={styles.contentWrapper}>
        <Container className={styles.container}>
          <div className={styles.content}>
            <div className={styles.anchorMenuWrapper}>
              <AnchorMenu className={styles.anchorMenu} links={links} />
            </div>
          </div>
        </Container>
      </div>
    </ModuleWrapper>
  );
};

InPageNavigationBar.displayName = 'InPageNavigationBar';

export default InPageNavigationBar;
